/**
 * Keyed reconciliation: diff two Edit snapshots (prev -> next) by id and write
 * the minimal field updates into the Y.Doc, in one transaction.
 *
 * This is how ALL local mutations reach the doc — the Zustand store keeps
 * producing plain immutable snapshots via immer (with normalization), and the
 * reconciler translates each snapshot transition into CRDT operations. The doc
 * therefore mirrors the latest snapshot exactly, at field-level granularity,
 * which is what gives concurrent edits field-level last-write-wins merges.
 *
 * Diffing never reads doc values (prev IS the doc's current content by
 * construction); the one exception is `ord` bookkeeping, which lives only in
 * the doc.
 */
import * as Y from 'yjs';
import type { AnyClip, Edit, Effect, Param, Source, Track } from '../format/types';
import {
  PARAM_MAP_KEYS,
  ROOT_META,
  ROOT_SOURCES,
  ROOT_TRACKS,
  ensureOrds,
  ordBetween,
} from './schema';

type YMap = Y.Map<unknown>;

interface EffectEntry {
  ord: string;
  effect: Omit<Effect, 'id'>;
}

/** Leaf equality: identity fast-path, then structural (leaves are small JSON). */
function leafEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Values stored in the doc are owned by the doc — decouple from snapshots. */
function clone<T>(v: T): T {
  return typeof v === 'object' && v !== null ? (structuredClone(v) as T) : v;
}

/** Pandora source urls are runtime-only; the doc never carries them. */
function persistedSource(s: Source): Source {
  return s.kind === 'pandora-video' && s.url !== null ? { ...s, url: null } : s;
}

function effectSansId(e: Effect): Omit<Effect, 'id'> {
  const { id: _id, ...rest } = e;
  return rest;
}

// ---------------------------------------------------------------------------
// Writers (fresh subtrees)
// ---------------------------------------------------------------------------

function writeParamMap(params: Record<string, Param>): YMap {
  const m = new Y.Map<unknown>();
  for (const [k, v] of Object.entries(params)) m.set(k, clone(v));
  return m;
}

function writeEffects(effects: Effect[]): YMap {
  const m = new Y.Map<unknown>();
  let ord = '';
  for (const e of effects) {
    ord = ordBetween(ord, null);
    const entry: EffectEntry = { ord, effect: clone(effectSansId(e)) };
    m.set(e.id, entry);
  }
  return m;
}

function writeClip(clipsMap: YMap, clip: AnyClip): void {
  const m = new Y.Map<unknown>();
  for (const [k, v] of Object.entries(clip)) {
    if (k === 'id') continue;
    if (k === 'effects') m.set(k, writeEffects(v as Effect[]));
    else if (PARAM_MAP_KEYS.has(k)) m.set(k, writeParamMap(v as Record<string, Param>));
    else m.set(k, clone(v));
  }
  clipsMap.set(clip.id, m);
}

function writeTrack(tracksMap: YMap, track: Track): void {
  const m = new Y.Map<unknown>();
  for (const [k, v] of Object.entries(track)) {
    if (k === 'id' || k === 'clips') continue;
    m.set(k, clone(v));
  }
  const clips = new Y.Map<unknown>();
  m.set('clips', clips);
  for (const clip of track.clips as AnyClip[]) writeClip(clips, clip);
  tracksMap.set(track.id, m);
}

// ---------------------------------------------------------------------------
// Diffing
// ---------------------------------------------------------------------------

function diffParamMap(m: YMap, prev: Record<string, Param>, next: Record<string, Param>): void {
  for (const [k, v] of Object.entries(next)) {
    if (!leafEqual(prev[k], v)) m.set(k, clone(v));
  }
}

function diffEffects(m: YMap, prev: Effect[], next: Effect[]): void {
  const prevById = new Map(prev.map((e) => [e.id, e]));
  const nextIds = new Set(next.map((e) => e.id));
  for (const e of prev) {
    if (!nextIds.has(e.id)) m.delete(e.id);
  }
  for (const e of next) {
    const before = prevById.get(e.id);
    if (before && leafEqual(effectSansId(before), effectSansId(e))) continue;
    const existing = m.get(e.id) as EffectEntry | undefined;
    const entry: EffectEntry = {
      ord: existing?.ord ?? '',
      effect: clone(effectSansId(e)),
    };
    m.set(e.id, entry);
  }
  // repair ords to match the authored array order, minimally
  ensureOrds(
    next.map((e) => e.id),
    (id) => {
      const entry = m.get(id) as EffectEntry | undefined;
      return entry && entry.ord !== '' ? entry.ord : undefined;
    },
    (id, ord) => {
      const entry = m.get(id) as EffectEntry;
      m.set(id, { ...entry, ord });
    },
  );
}

function diffClip(m: YMap, prev: AnyClip, next: AnyClip): void {
  for (const [k, v] of Object.entries(next)) {
    if (k === 'id') continue;
    if (k === 'effects') {
      diffEffects(m.get(k) as YMap, (prev as { effects?: Effect[] }).effects ?? [], v as Effect[]);
    } else if (PARAM_MAP_KEYS.has(k)) {
      diffParamMap(
        m.get(k) as YMap,
        ((prev as unknown as Record<string, unknown>)[k] ?? {}) as Record<string, Param>,
        v as Record<string, Param>,
      );
    } else if (!leafEqual((prev as unknown as Record<string, unknown>)[k], v)) {
      m.set(k, clone(v));
    }
  }
  for (const k of Object.keys(prev)) {
    if (k !== 'id' && !(k in next)) m.delete(k);
  }
}

function diffClips(m: YMap, prev: AnyClip[], next: AnyClip[]): void {
  const prevById = new Map(prev.map((c) => [c.id, c]));
  const nextById = new Map(next.map((c) => [c.id, c]));
  for (const id of prevById.keys()) {
    if (!nextById.has(id)) m.delete(id);
  }
  for (const [id, clip] of nextById) {
    const before = prevById.get(id);
    if (!before) writeClip(m, clip);
    else if (before !== clip) diffClip(m.get(id) as YMap, before, clip);
  }
}

function diffTrack(m: YMap, prev: Track, next: Track): void {
  for (const [k, v] of Object.entries(next)) {
    if (k === 'id' || k === 'clips') continue;
    if (!leafEqual((prev as unknown as Record<string, unknown>)[k], v)) m.set(k, clone(v));
  }
  for (const k of Object.keys(prev)) {
    if (k !== 'id' && k !== 'clips' && !(k in next)) m.delete(k);
  }
  diffClips(m.get('clips') as YMap, prev.clips as AnyClip[], next.clips as AnyClip[]);
}

function diffTracks(tracksMap: YMap, prev: Track[], next: Track[]): void {
  const prevById = new Map(prev.map((t) => [t.id, t]));
  const nextById = new Map(next.map((t) => [t.id, t]));
  for (const id of prevById.keys()) {
    if (!nextById.has(id)) tracksMap.delete(id);
  }
  for (const [id, track] of nextById) {
    const before = prevById.get(id);
    if (!before) writeTrack(tracksMap, track);
    else if (before !== track) diffTrack(tracksMap.get(id) as YMap, before, track);
  }
  // authored track order (z-order) lives in the doc as fractional ords
  ensureOrds(
    next.map((t) => t.id),
    (id) => (tracksMap.get(id) as YMap).get('ord') as string | undefined,
    (id, ord) => (tracksMap.get(id) as YMap).set('ord', ord),
  );
}

function diffSources(m: YMap, prev: Record<string, Source>, next: Record<string, Source>): void {
  for (const id of Object.keys(prev)) {
    if (!(id in next)) m.delete(id);
  }
  for (const [id, src] of Object.entries(next)) {
    const before = prev[id];
    if (before === src) continue; // identity: unchanged (urls resolve out of band)
    const stripped = persistedSource(src);
    if (before && leafEqual(persistedSource(before), stripped)) continue;
    m.set(id, clone(stripped));
  }
}

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

const EMPTY_BASE: Edit = {
  type: 'fabled-edit',
  version: 2,
  settings: null as unknown as Edit['settings'], // forces the first settings write
  sources: {},
  tracks: [],
};

/**
 * Apply the prev -> next snapshot transition to the doc as one transaction
 * under `origin`. `prev` must be the snapshot the doc currently mirrors.
 */
export function reconcileEdit(doc: Y.Doc, prev: Edit, next: Edit, origin: unknown): void {
  if (prev === next) return;
  doc.transact(() => {
    const meta = doc.getMap(ROOT_META);
    if (meta.get('version') !== next.version) meta.set('version', next.version);
    if (!leafEqual(prev.settings, next.settings)) meta.set('settings', clone(next.settings));
    diffSources(doc.getMap(ROOT_SOURCES), prev.sources, next.sources);
    diffTracks(doc.getMap(ROOT_TRACKS), prev.tracks, next.tracks);
  }, origin);
}

/** Fresh doc mirroring `edit` (used at load and by tests). */
export function buildDocFromEdit(edit: Edit, origin: unknown): Y.Doc {
  const doc = new Y.Doc();
  reconcileEdit(doc, EMPTY_BASE, edit, origin);
  return doc;
}
